//-- Copyright (c) 2021 TMT Insights Group, LLC
import { ChangeEvent, useContext, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Box, IconButton, Pagination as MuiPagination, Skeleton, Tooltip, Typography } from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getDailyReportData, getDailyReportDataNew, getGeneratedReportData } from '@/redux/slices/dashboardSlice';
import {
  selectDailyReportData,
  selectDailyReportStatus,
  selectGeneratedReportData,
  selectGeneratedReportStatus
} from '@/redux/selectors/dashboardSelectors';
import { LoadingStatuses } from '@/constants/constants';
import { Spinner } from '@/components/_common';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import { DailyReportsHeaderRow } from '@/components/Dashboard/Widgets/Default/DailyReports/DailyReportsHeaderRow';
import { NoDataMessage } from '@/components/Dashboard/Widgets/Default/DailyReports/NoDataMessage';
import { ReportFile } from '@/components/Dashboard/Widgets/Default/DailyReports/ReportFile';
import SlideToggle from '@/components/_common/SlideToggle';

export type RowColumns = {
  [key: string]: {
    label: string;
    cols: number;
    align: 'flex-start' | 'flex-end' | 'center';
    order_on?: string;
    sortable: boolean;
  };
};

type DailyReportSettings = {
  reports_key: string;
  pageSize: number;
  columns: RowColumns;
};

/*

TODO: Enable sorting on this component by...
1 - update the 'order_on' attribute in the settings object to correctly identify which
    key to use for each column when sorting by that label
2 - uncomment the onClick handler in the ColumnLabel component:
    // onClick={() => handleLabelClick(key)}
3 - remove the hardcoded values in the slice
*/

function DailyReportsWidget() {
  const dispatch = useAppDispatch();
  const settings: DailyReportSettings = {
    reports_key: 'report_dls',
    pageSize: 10,
    columns: {
      file_name: {
        sortable: false,
        label: 'File Name',
        cols: 6,
        align: 'flex-start',
        order_on: 'file_name'
      },
      created_by: {
        sortable: false,
        label: 'Created By',
        cols: 2,
        align: 'flex-start'
      },
      created_at: {
        sortable: false,
        label: 'Created',
        cols: 2,
        align: 'flex-start',
        order_on: 'created_at'
      },
      file_size: {
        sortable: false,
        label: 'File Size',
        cols: 2,
        align: 'flex-start',
        order_on: 'file_size'
      },
      action: {
        sortable: false,
        label: 'Action',
        cols: 2,
        align: 'center'
      }
    }
  };

  const asyncReports = useContext(ConfigContext)?.dashboardConfig.graphSettings.reports?.asyncReports || false;
  const dailyReportData = useAppSelector(selectDailyReportData);
  const generatedReportData = asyncReports ? useAppSelector(selectGeneratedReportData) : null;
  const dailyReportState = useAppSelector(selectDailyReportStatus);
  const generatedReportState = asyncReports ? useAppSelector(selectGeneratedReportStatus) : null;
  const isDataLoading = dailyReportState === LoadingStatuses.pending;
  const dailyReportsCount = useContext(ConfigContext)?.dashboardConfig.maximumDailyReports;
  const [reportView, setReportView] = useState<'daily' | 'generated'>('daily');
  const reportState = useMemo(
    () => (reportView === 'daily' ? dailyReportState : generatedReportState),
    [dailyReportState, generatedReportState, reportView]
  );
  const reportsList = useMemo(() => {
    if (reportView === 'daily')
      return dailyReportData?.data?.length && dailyReportsCount ? dailyReportData.data.slice(0, dailyReportsCount) : dailyReportData?.data;
    if (reportView === 'generated') return generatedReportData?.data;
    return [];
  }, [dailyReportData, generatedReportData, reportView]);
  const [view, setView] = useState('loading');
  const [downloadedReports, setDownloadedReports] = useState<string[]>([]);
  const totalPages = useMemo(
    () =>
      reportView === 'daily'
        ? Math.ceil(dailyReportData?.pagination.total / settings.pageSize)
        : Math.ceil((generatedReportData?.pagination.total || 0) / settings.pageSize),
    [dailyReportData, generatedReportData, reportView]
  );
  const currentPage = useMemo(
    () => (reportView === 'daily' ? dailyReportData?.pagination.page || 0 : generatedReportData?.pagination.page || 0),
    [dailyReportData, generatedReportData, reportView]
  );

  const fetchReports = (resetPage = false, viewOverride?: 'daily' | 'generated') => {
    const curView = viewOverride || reportView;
    if (curView === 'daily') {
      if (asyncReports) {
        dispatch(getDailyReportDataNew({ page: resetPage ? 0 : dailyReportData?.pagination?.page || 0, pageSize: settings.pageSize }));
      } else {
        dispatch(getDailyReportData({ page: resetPage ? 0 : dailyReportData?.pagination?.page || 0, pageSize: settings.pageSize }));
      }
    } else {
      dispatch(getGeneratedReportData({ page: resetPage ? 0 : generatedReportData?.pagination?.page || 0, pageSize: settings.pageSize }));
    }
  };

  const handleReportViewChange = (view: 'daily' | 'generated') => {
    setReportView(view);
    fetchReports(true, view);
  };

  useEffect(() => {
    // if download history exists in local storage, set it
    const history = JSON.parse(localStorage.getItem(settings.reports_key) || '[]');
    if (history) setDownloadedReports(history);
    fetchReports();
  }, []);

  useEffect(() => {
    localStorage.setItem(settings.reports_key, JSON.stringify(downloadedReports));
  }, [downloadedReports]);

  const isDownloaded = (fileKey: string) => (!!downloadedReports.find((key) => key === fileKey));

  useEffect(() => {
    switch (reportState) {
      case LoadingStatuses.fulfilled:
        if (!reportsList?.length) {
          setView('no-data');
        } else {
          setView('reports');
        }
        break;
      case LoadingStatuses.rejected:
        setView('error');
        break;
    }
  }, [reportState, dailyReportData, generatedReportData]);

  const handlePaginationChange = (_: ChangeEvent<unknown>, value: number) => {
    // Mui Pagination starts at 1, but our API starts at 0
    const pagination = {
      page: value - 1,
      pageSize: settings.pageSize
    };
    if (asyncReports) {
      if (reportView === 'daily') {
        dispatch(getDailyReportDataNew(pagination));
      } else {
        dispatch(getGeneratedReportData(pagination));
      }
    } else {
      dispatch(getDailyReportData(pagination));
    }
  };

  const getView = () => {
    switch (view) {
      case 'loading':
        return <DailyReportingSkeleton />;
      case 'reports':
        if (reportsList?.length) {
          return (
            <DailyReportsTable data-testid="reports-table">
              <DailyReportsHeaderRow
                includeCreatedBy={!!reportsList[0]?.created_by}
                columns={settings.columns}
                offsetForScrollbar={reportsList.length > 4}
              />
              <ScrollableDiv>
                <Library loading={`${isDataLoading}`}>
                  {isDataLoading && <Spinner />}
                  {!!reportsList &&
                    reportsList.map((report) => (
                      <ReportFile
                        key={report.id}
                        columns={settings.columns}
                        report={report}
                        isDownloaded={isDownloaded(report.id)}
                        includeCreatedBy={!!reportsList[0]?.created_by}
                        asyncView={reportView === 'generated' || (reportView === 'daily' && asyncReports)}
                        downloadCallback={() => {
                          if (!isDownloaded(report.id)) {
                            setDownloadedReports([...downloadedReports, report.id]);
                          }
                        }}
                      />
                    ))}
                </Library>
              </ScrollableDiv>
              <PaginationWrapper>
                {totalPages > 1 && (!dailyReportsCount || reportView === 'generated') && (
                  <MuiPagination
                    disabled={!reportsList.length}
                    count={totalPages || 1}
                    page={currentPage + 1}
                    onChange={handlePaginationChange}
                    size="small"
                  />
                )}
              </PaginationWrapper>
            </DailyReportsTable>
          );
        }
        break;
      case 'no-data':
        return <NoDataMessage />;
      case 'error':
      default:
        return <Typography>Error Displaying Reports</Typography>;
    }
  };

  return (
    <TableWrapper>
      {asyncReports && (
        <HeaderExtra>
          <Tooltip title="Refresh Report Data">
            <IconButton onClick={() => fetchReports()}>
              <PolarisIcon icon="refresh" />
            </IconButton>
          </Tooltip>
          <SlideToggle
            left={{ label: 'Daily', value: 'daily' }}
            right={{ label: 'Generated', value: 'generated' }}
            callback={(key) => handleReportViewChange(key as 'daily' | 'generated')}
            alternateColors
            defaultUnchecked
          />
        </HeaderExtra>
      )}
      {getView()}
    </TableWrapper>
  );
}

export default DailyReportsWidget;

const DailyReportingSkeleton = () => (
  <SkeletonWrapper data-testid="skeleton">
    <Skeleton variant="text" width={'100%'} height={25} />
    <TableSkeleton>
      <Skeleton variant="rectangular" width={'100%'} height={'100%'} />
    </TableSkeleton>
    <Skeleton variant="text" width={'100%'} height={25} />
  </SkeletonWrapper>
);

const TableWrapper = styled.div`
  height: 100%;
  width: 100%;
  padding: ${({ theme }) => theme.spacing(2, 4)};
`;

const DailyReportsTable = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Library = styled.div<{ loading: string }>`
  flex-grow: 1;
  max-height: 270px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  row-gap: 10px;
  opacity: ${({ loading }) => (loading === 'true' ? 0.5 : 1)};
  transition: all 0.2s ease-in-out;
`;

const PaginationWrapper = styled.div`
  padding-top: ${({ theme }) => theme.spacing(2)};
  display: flex;
  flex-direction: row;
  justify-content: right;
`;

const SkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const TableSkeleton = styled.div`
  flex-grow: 1;
  margin: ${({ theme }) => theme.spacing(1, 0)};
`;

const ScrollableDiv = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  min-height: 270px;
`;

const HeaderExtra = styled(Box)`
  position: absolute;
  top: 47px;
  right: 30px;
  display: flex;
`;
